import { useEffect, useMemo, useState } from 'react';
import { Download, ExternalLink, Search, X } from 'lucide-react';
import type { PaymentRow } from '@contracts';
import { ODOO_WEB_URL } from '@/lib/config';
import { canOpenOdooLinks } from '@/services/authService';
import { exportExcel } from '@/lib/exportData';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtDay = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const orderUrl = (id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=pos.order&view_type=form`;
const paymentUrl = (id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=pos.payment&view_type=form`;

/** Un pago cuenta como real solo si su orden no es refund, no fue devuelta y no es $0. */
const isRealPay = (p: PaymentRow) => !p.isRefund && !p.wasRefunded && !p.isZero;

type Cat = 'all' | 'cash' | 'card';
type SortCol = 'method' | 'order' | 'pos' | 'customer' | 'amount';
type SortDir = 'asc' | 'desc';
const PAGE_OPTS = [10, 50, 100, 0];

export interface PaymentsDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  payments: PaymentRow[];
  loading?: boolean;
}

/** Drill-down de PAGOS (widget Transacciones): el front solo pinta + filtra/ordena/pagina. */
export function PaymentsDrilldownModal({ open, onClose, title, payments, loading }: PaymentsDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState<Cat>('all');
  const [sortCol, setSortCol] = useState<SortCol>('amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [q, cat, sortCol, sortDir, pageSize, payments]);

  const counts = useMemo(
    () => ({
      all: payments.length,
      cash: payments.filter((p) => p.category === 'cash').length,
      card: payments.filter((p) => p.category === 'card').length,
    }),
    [payments],
  );
  const orderCount = useMemo(() => new Set(payments.map((p) => p.orderId)).size, [payments]);
  // Regla maestra: netos = pagos de ventas reales; brutos = de ventas positivas (incluye devueltas).
  const netTotal = useMemo(() => payments.filter(isRealPay).reduce((s, p) => s + p.amount, 0), [payments]);

  const toggleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = cat === 'all' ? payments : payments.filter((p) => p.category === cat);
    if (term) {
      rows = rows.filter((p) =>
        [p.method, p.orderRef, p.customer, p.posName, p.cashier].some((f) => f?.toLowerCase().includes(term)),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const str = (a: string, b: string) => a.localeCompare(b) * dir;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sortCol) {
        case 'amount': return (a.amount - b.amount) * dir;
        case 'method': return str(a.method, b.method);
        case 'pos': return str(a.posName, b.posName);
        case 'customer': return str(a.customer, b.customer);
        default: return str(a.orderRef, b.orderRef);
      }
    });
    return sorted;
  }, [payments, q, cat, sortCol, sortDir]);

  const netF = useMemo(() => filtered.filter(isRealPay).reduce((s, p) => s + p.amount, 0), [filtered]);
  const grossRows = useMemo(() => filtered.filter((p) => !p.isRefund && !p.isZero), [filtered]);
  const grossF = useMemo(() => grossRows.reduce((s, p) => s + p.amount, 0), [grossRows]);
  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const rows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  const doExport = () =>
    void exportExcel(
      'pagos',
      filtered.map((p) => ({
        Método: p.method,
        Tipo: p.category === 'cash' ? 'Efectivo' : 'Tarjeta',
        Orden: p.orderRef,
        Sucursal: p.posName,
        Cajero: p.cashier,
        Cliente: p.customer,
        Fecha: p.date,
        Monto: p.amount,
      })),
    );

  if (!open) return null;

  const arrow = (col: SortCol) => (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const chip = (v: Cat, label: string, n: number) => (
    <button
      onClick={() => setCat(v)}
      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
        cat === v ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {label} ({n})
    </button>
  );
  const cols: { col: SortCol; label: string; right: boolean }[] = [
    { col: 'method', label: 'Método', right: false },
    { col: 'order', label: 'Folio', right: false },
    { col: 'pos', label: 'Sucursal · Cajero', right: false },
    { col: 'customer', label: 'Cliente', right: false },
    { col: 'amount', label: 'Monto', right: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-sky-700 via-blue-600 to-indigo-600 px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15 text-2xl">🛒</div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{counts.all} pagos</span>
              {' · '}{orderCount} órdenes{' · '}<span className="font-semibold text-white">{money(netTotal)}</span> netos
            </p>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          <div className="flex gap-1">
            {chip('all', 'Todos', counts.all)}
            {chip('cash', 'Efectivo', counts.cash)}
            {chip('card', 'Tarjeta', counts.card)}
          </div>
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar método, folio, cliente, banco…"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-sky-400"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
            <div className="flex gap-1">
              {PAGE_OPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPageSize(p)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    pageSize === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p === 0 ? 'Todos' : p}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={doExport}
            title="Descargar Excel"
            className="flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600"
          >
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>

        {/* Tabla */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando pagos…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin pagos para los filtros actuales.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  {cols.map((h) => (
                    <th
                      key={h.col}
                      onClick={() => toggleSort(h.col)}
                      className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-sky-600 ${h.right ? 'text-right' : ''}`}
                    >
                      {h.label}
                      <span className="ml-0.5 inline-block w-2 text-sky-600">{arrow(h.col)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const accent = p.wasRefunded
                    ? 'border-l-rose-400 bg-rose-50/60'
                    : p.isRefund
                      ? 'border-l-amber-400 bg-amber-50/60'
                      : p.isZero
                        ? 'border-l-slate-300 bg-slate-50'
                        : 'border-l-transparent hover:bg-slate-50';
                  const tag = p.wasRefunded
                    ? { label: `Devuelta ${fmtDay(p.refundedAt)}`, cls: 'bg-rose-100 text-rose-700' }
                    : p.isRefund
                      ? { label: `Devolución${p.refundOfDate ? ` · de ${fmtDay(p.refundOfDate)}` : ''}`, cls: 'bg-amber-100 text-amber-700' }
                      : p.isZero
                        ? { label: 'Garantía / $0', cls: 'bg-slate-200 text-slate-600' }
                        : null;
                  return (
                    <tr key={p.id} className={`border-l-4 border-b border-slate-100 ${accent}`}>
                      <td className="px-4 py-2.5">
                        {canOpenOdooLinks() ? (
                          <a
                            href={paymentUrl(p.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir el pago en Odoo"
                            className="group inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-sky-100 hover:text-sky-800"
                          >
                            <span className={`h-2 w-2 rounded-full ${p.category === 'cash' ? 'bg-emerald-500' : 'bg-sky-500'}`} />
                            {p.method}
                            <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                            <span className={`h-2 w-2 rounded-full ${p.category === 'cash' ? 'bg-emerald-500' : 'bg-sky-500'}`} />
                            {p.method}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {canOpenOdooLinks() ? (
                          <a
                            href={orderUrl(p.orderId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir en Odoo"
                            className="group inline-flex items-center gap-1 font-mono text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                          >
                            {p.orderRef}
                            <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                          </a>
                        ) : (
                          <span className="font-mono text-xs font-semibold text-slate-700">{p.orderRef}</span>
                        )}
                        {tag && (
                          <span className={`ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${tag.cls}`}>
                            {tag.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <p className="text-slate-700">{p.posName}</p>
                        <p className="text-[11px] text-slate-400">{p.cashier} · {fmtDate(p.date)}</p>
                      </td>
                      <td className="px-4 py-2.5">{p.customer}</td>
                      <td
                        className={`px-4 py-2.5 text-right font-bold tabular-nums ${
                          p.isRefund ? 'text-rose-600' : p.wasRefunded ? 'text-slate-400 line-through' : 'text-sky-700'
                        }`}
                      >
                        {money(p.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer: Pagos brutos − Devueltos = Netos (regla maestra) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          <span className="text-slate-500">
            Pagos brutos ({grossRows.length}):{' '}
            <span className="font-semibold text-slate-700">{money(grossF)}</span>
          </span>
          {grossF - netF > 0 && (
            <span className="text-rose-500">
              − Devueltos: <span className="font-semibold">{money(grossF - netF)}</span>
            </span>
          )}
          <span className="text-slate-600">
            = Pagos netos ({filtered.filter(isRealPay).length}):{' '}
            <span className="font-bold text-emerald-700">{money(netF)}</span>
          </span>
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">
              {pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length} pagos`}
            </span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">‹</button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
